/**
 * Health Score Details Page
 * 
 * Detailed view for a single franchise partner's health scores with history, trends, and comparisons
 */

import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Chip
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  Compare as CompareIcon,
  Info as InfoIcon
} from '@mui/icons-material'
import AdminPageHeader from '../../components/admin/shared/AdminPageHeader'
import { healthScoreApi, FranchiseHealthScore } from '../../services/healthScoreApi'
import { useDataFetching } from '../../hooks/useDataFetching'
import { HealthScoreBadge } from '../../components/HealthScoreBadge'
import HealthScoreHistory from '../../components/health-scores/HealthScoreHistory'
import HealthScoreTrends from '../../components/health-scores/HealthScoreTrends'
import HealthScoreComparison from '../../components/health-scores/HealthScoreComparison'
import HealthScoreTooltip from '../../components/HealthScoreTooltip'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`health-score-tabpanel-${index}`}
      aria-labelledby={`health-score-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  )
}

function a11yProps(index: number) {
  return {
    id: `health-score-tab-${index}`,
    'aria-controls': `health-score-tabpanel-${index}`,
  }
}

export const HealthScoreDetails: React.FC = () => {
  const { fpCode } = useParams<{ fpCode: string }>()
  const navigate = useNavigate()
  
  const [tabValue, setTabValue] = useState(0)

  // Fetch the health scores and pick out this franchise (see useDataFetching)
  const { data: franchiseData, loading, error } = useDataFetching<FranchiseHealthScore>({
    fetchFn: async () => {
      if (!fpCode) {
        throw new Error('Franchise partner code is required')
      }
      const healthScores = await healthScoreApi.getHealthScores()
      const franchise = healthScores.franchises.find(
        fp => fp.fp_code.toLowerCase() === fpCode.toLowerCase()
      )
      if (!franchise) {
        throw new Error(`Franchise partner ${fpCode} not found`)
      }
      return franchise
    },
    dependencies: [fpCode],
  })

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  const handleBackClick = () => {
    navigate('/admin/health-scores')
  }

  if (loading || (!franchiseData && !error)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">
            Loading health score details...
          </Typography>
        </Stack>
      </Box>
    )
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackClick}
        >
          Back to Health Scores
        </Button>
      </Box>
    )
  }

  if (!franchiseData || !fpCode) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Franchise partner data not available
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackClick}
        >
          Back to Health Scores
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      <AdminPageHeader
        pageKey="healthScores"
        titleOverride={franchiseData.franchise_name}
        descriptionOverride={`${franchiseData.fp_code} • ${franchiseData.country} • ${franchiseData.region}`}
        breadcrumbs={[
          { label: 'Admin', to: '/admin' },
          { label: 'Health Scores', to: '/admin/health-scores' },
          { label: franchiseData.franchise_name },
        ]}
        actions={
          <Stack direction="row" spacing={1}>
            <Chip
              label={franchiseData.fp_status}
              color={franchiseData.fp_status === 'Active' ? 'success' : 'default'}
              variant="outlined"
            />
            {franchiseData.last_sync_date && (
              <Chip
                label={`Last sync: ${new Date(franchiseData.last_sync_date).toLocaleDateString()}`}
                variant="outlined"
              />
            )}
          </Stack>
        }
      />

      {/* Health Score Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" color="text.secondary">
                  Overall Health
                </Typography>
                <HealthScoreTrends
                  fpCode={fpCode}
                  domain="overall"
                  size="medium"
                  showChangeValue
                />
              </Box>
              <Box display="flex" justifyContent="center">
                <HealthScoreBadge
                  band={franchiseData.overall_health.band}
                  color={franchiseData.overall_health.color}
                  score={franchiseData.overall_health.score}
                  size="medium"
                  showIcon
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" color="text.secondary">
                  📦 Products
                </Typography>
                <HealthScoreTrends
                  fpCode={fpCode}
                  domain="product"
                  size="medium"
                  showChangeValue
                />
              </Box>
              <Box display="flex" justifyContent="center">
                {franchiseData.product_score ? (
                  <HealthScoreTooltip fp_code={fpCode} domain="product">
                    <div>
                      <HealthScoreBadge
                        band={franchiseData.product_score.band}
                        color={franchiseData.product_score.color}
                        score={franchiseData.product_score.score}
                        size="medium"
                      />
                    </div>
                  </HealthScoreTooltip>
                ) : (
                  <HealthScoreBadge
                    band="No Data"
                    color="grey"
                    score={null}
                    size="medium"
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" color="text.secondary">
                  🧾 Sales
                </Typography>
                <HealthScoreTrends
                  fpCode={fpCode}
                  domain="sale"
                  size="medium"
                  showChangeValue
                />
              </Box>
              <Box display="flex" justifyContent="center">
                {franchiseData.sale_score ? (
                  <HealthScoreTooltip fp_code={fpCode} domain="sale">
                    <div>
                      <HealthScoreBadge
                        band={franchiseData.sale_score.band}
                        color={franchiseData.sale_score.color}
                        score={franchiseData.sale_score.score}
                        size="medium"
                      />
                    </div>
                  </HealthScoreTooltip>
                ) : (
                  <HealthScoreBadge
                    band="No Data"
                    color="grey"
                    score={null}
                    size="medium"
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" color="text.secondary">
                  🔧 System
                </Typography>
                <HealthScoreTrends
                  fpCode={fpCode}
                  domain="system"
                  size="medium"
                  showChangeValue
                />
              </Box>
              <Box display="flex" justifyContent="center">
                {franchiseData.system_score ? (
                  <HealthScoreTooltip fp_code={fpCode} domain="system">
                    <div>
                      <HealthScoreBadge
                        band={franchiseData.system_score.band}
                        color={franchiseData.system_score.color}
                        score={franchiseData.system_score.score}
                        size="medium"
                      />
                    </div>
                  </HealthScoreTooltip>
                ) : (
                  <HealthScoreBadge
                    band="No Data"
                    color="grey"
                    score={null}
                    size="medium"
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* System Connectivity */}
      {franchiseData.system_connectivity && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              🔗 System Connectivity
            </Typography>
            <Stack direction="row" spacing={2}>
              <Chip
                label="Lightspeed"
                color={franchiseData.system_connectivity.lightspeed ? 'success' : 'default'}
                variant={franchiseData.system_connectivity.lightspeed ? 'filled' : 'outlined'}
              />
              <Chip
                label="Xero"
                color={franchiseData.system_connectivity.xero ? 'info' : 'default'}
                variant={franchiseData.system_connectivity.xero ? 'filled' : 'outlined'}
              />
              <Chip
                label={franchiseData.country === 'US' ? 'Skimmer' : 'Pooltrackr'}
                color={franchiseData.system_connectivity.field_management ? 'warning' : 'default'}
                variant={franchiseData.system_connectivity.field_management ? 'filled' : 'outlined'}
              />
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="health score details tabs">
            <Tab
              label="History"
              icon={<AssessmentIcon />}
              iconPosition="start"
              {...a11yProps(0)}
            />
            <Tab
              label="Trends"
              icon={<TrendingUpIcon />}
              iconPosition="start"
              {...a11yProps(1)}
            />
            <Tab
              label="Compare"
              icon={<CompareIcon />}
              iconPosition="start"
              {...a11yProps(2)}
            />
            <Tab
              label="About"
              icon={<InfoIcon />}
              iconPosition="start"
              {...a11yProps(3)}
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <HealthScoreHistory
            fpCode={fpCode}
            franchiseName={franchiseData.franchise_name}
            initialDays={30}
            height={500}
            showExceptionCount
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    📅 Weekly Trends
                  </Typography>
                  <Stack spacing={3}>
                    <HealthScoreTrends
                      fpCode={fpCode}
                      domain="overall"
                      period="week"
                      size="large"
                      showChangeValue
                      showSparkline
                    />
                    <HealthScoreTrends
                      fpCode={fpCode}
                      domain="product"
                      period="week"
                      size="large"
                      showChangeValue
                      showSparkline
                    />
                    <HealthScoreTrends
                      fpCode={fpCode}
                      domain="sale"
                      period="week"
                      size="large"
                      showChangeValue
                      showSparkline
                    />
                    <HealthScoreTrends
                      fpCode={fpCode}
                      domain="system"
                      period="week"
                      size="large"
                      showChangeValue
                      showSparkline
                    />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    📆 Monthly Trends
                  </Typography>
                  <Stack spacing={3}>
                    <HealthScoreTrends
                      fpCode={fpCode}
                      domain="overall"
                      period="month"
                      size="large"
                      showChangeValue
                      showSparkline
                    />
                    <HealthScoreTrends
                      fpCode={fpCode}
                      domain="product"
                      period="month"
                      size="large"
                      showChangeValue
                      showSparkline
                    />
                    <HealthScoreTrends
                      fpCode={fpCode}
                      domain="sale"
                      period="month"
                      size="large"
                      showChangeValue
                      showSparkline
                    />
                    <HealthScoreTrends
                      fpCode={fpCode}
                      domain="system"
                      period="month"
                      size="large"
                      showChangeValue
                      showSparkline
                    />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <HealthScoreComparison
            initialFpCodes={[fpCode]}
            initialMetric="overall"
            height={500}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📋 About Health Scores
              </Typography>
              <Typography variant="body1" paragraph>
                Health scores provide a quantitative measure of data quality and system health for franchise partners.
                Scores are calculated based on exception detection rules and range from 0 (worst) to 100 (best).
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                🎯 Score Bands
              </Typography>
              <Stack spacing={1}>
                <Box display="flex" alignItems="center" gap={2}>
                  <HealthScoreBadge band="Good" color="green" size="small" />
                  <Typography variant="body2">0-25: Excellent data health, minimal issues</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={2}>
                  <HealthScoreBadge band="Watch" color="orange" size="small" />
                  <Typography variant="body2">25-50: Minor issues that need attention</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={2}>
                  <HealthScoreBadge band="Poor" color="red" size="small" />
                  <Typography variant="body2">50-75: Significant issues requiring action</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={2}>
                  <HealthScoreBadge band="Critical" color="black" size="small" />
                  <Typography variant="body2">75+: Severe issues needing immediate attention</Typography>
                </Box>
              </Stack>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                📊 Domain Categories
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2">
                  <strong>📦 Product Health:</strong> Inventory discrepancies, pricing issues, missing products
                </Typography>
                <Typography variant="body2">
                  <strong>🧾 Sale Health:</strong> Voided transactions, payment mismatches, sync failures
                </Typography>
                <Typography variant="body2">
                  <strong>🔧 System Health:</strong> API connectivity, authentication, configuration issues
                </Typography>
                <Typography variant="body2">
                  <strong>🎯 Overall Health:</strong> Weighted average across all domains
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </TabPanel>
      </Card>
    </Box>
  )
}

export default HealthScoreDetails